/**
 * Cookie-consent state management.
 *
 * Single source of truth for whether the user has granted consent to
 * analytics tracking (PostHog). Self-hosted — no Cookiebot / OneTrust
 * dependency to avoid recurring cost and external script weight.
 *
 * Storage:
 *   Cookie name:  `wtm_consent`
 *   Value shape:  { analytics: boolean, timestamp: ISO date }
 *   Expiry:       12 months from acceptance; re-prompts after expiry
 *   Path:         /
 *
 * Why a cookie (not localStorage):
 *   - Cookies are accessible to both server-side rendering paths and
 *     the client. localStorage is client-only.
 *   - Cookies respect the user's expectation that "cookie banner"
 *     manages a cookie.
 *   - Cookie expiry semantics give us a natural re-prompt cadence.
 *
 * The consent state itself is NOT a tracking cookie — it's a strictly
 * necessary cookie under GDPR Art. 5(3) (operational necessity to
 * remember the user's preference). The strict-necessary classification
 * is why this cookie is set BEFORE consent is granted (it has to be —
 * we need to remember "rejected" too).
 */

const COOKIE_NAME = "wtm_consent";
const COOKIE_EXPIRY_DAYS = 365; // 12 months

export interface ConsentState {
  analytics: boolean;
  timestamp: string; // ISO date
}

/**
 * Read the stored consent state. Returns `null` when no decision has
 * been made yet — that's the cue to show the banner. SSR-safe: returns
 * `null` when `document` is unavailable (server-side).
 */
export function getConsent(): ConsentState | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]*)`),
  );
  if (!match) return null;
  try {
    const raw = decodeURIComponent(match[1]);
    const parsed = JSON.parse(raw) as ConsentState;
    if (
      typeof parsed.analytics !== "boolean" ||
      typeof parsed.timestamp !== "string"
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Persist a consent decision. Setting either `true` or `false`
 * removes the banner — the user has expressed a choice.
 *
 * Side effect: writes a window-level event `wtm:consent-changed` so
 * subscribers (e.g., the PostHog client) can react without polling.
 */
export function setConsent(analytics: boolean): void {
  if (typeof document === "undefined") return;
  const state: ConsentState = {
    analytics,
    timestamp: new Date().toISOString(),
  };
  const value = encodeURIComponent(JSON.stringify(state));
  const expires = new Date();
  expires.setDate(expires.getDate() + COOKIE_EXPIRY_DAYS);
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "; Secure"
      : "";
  document.cookie = `${COOKIE_NAME}=${value}; path=/; expires=${expires.toUTCString()}; SameSite=Lax${secure}`;

  // Fire a custom event so the PostHog provider can pick up consent
  // grants without remounting or polling.
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("wtm:consent-changed", { detail: state }),
    );
  }
}

/**
 * Convenience read for callers that only care about the analytics
 * flag. Returns `false` when no decision has been made yet — we
 * default to OFF until the user explicitly opts in.
 */
export function hasAnalyticsConsent(): boolean {
  return getConsent()?.analytics === true;
}

/**
 * Clear the stored consent (re-prompts on next page load). Wired to
 * a debug-only flow; not exposed in the regular UI.
 */
export function clearConsent(): void {
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT`;
}
